import React, { useState, useEffect } from 'react';
import { useAuth, API } from '../App';
import { DashboardLayout } from '../components/DashboardLayout';
import { motion } from 'framer-motion';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';
import { 
  Compass, 
  Lock, 
  Loader2,
  TrendingUp,
  Target,
  Trophy,
  Lightbulb,
  Star,
  Calendar,
  CheckCircle,
  AlertCircle
} from 'lucide-react';
import { Progress } from '../components/ui/progress';

const QuarterlyReview = () => {
  const { token, user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [eligibility, setEligibility] = useState(null);
  const [reviews, setReviews] = useState([]);
  const [currentReview, setCurrentReview] = useState(null);

  useEffect(() => {
    checkEligibility();
    fetchReviews();
  }, []);

  const checkEligibility = async () => {
    try {
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      const response = await axios.get(`${API}/quarterly/eligibility`, { headers });
      setEligibility(response.data);
    } catch (error) {
      console.error('Error checking eligibility:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchReviews = async () => {
    try {
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      const response = await axios.get(`${API}/quarterly/reviews`, { headers });
      setReviews(response.data.reviews || []);
      if (response.data.reviews?.length > 0) {
        setCurrentReview(response.data.reviews[0]);
      }
    } catch (error) {
      console.error('Error fetching reviews:', error);
    }
  };

  const handleGenerateReview = async () => {
    setGenerating(true);
    try {
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      const response = await axios.post(`${API}/quarterly/generate`, {}, { headers });
      setCurrentReview(response.data);
      toast.success('Quarterly review generated!');
      fetchReviews();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to generate review');
    } finally {
      setGenerating(false);
    }
  };

  if (loading) {
    return (
      <DashboardLayout title="Quarterly Vision Review">
        <div className="flex items-center justify-center py-20">
          <Loader2 className="animate-spin text-[#D4AF37]" size={32} />
        </div>
      </DashboardLayout>
    );
  }

  // Locked State
  if (!eligibility?.eligible) {
    return (
      <DashboardLayout title="Quarterly Vision Review">
        <div className="max-w-2xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="card-premium p-12 text-center"
          >
            <div className="w-20 h-20 mx-auto mb-6 bg-[#1C1C1C] flex items-center justify-center">
              <Lock className="text-[#52525B]" size={32} />
            </div>
            
            <h2 className="font-serif text-2xl text-white mb-4">
              Quarterly Vision Review
            </h2>
            <p className="text-[#A1A1AA] mb-8">
              This exclusive feature unlocks after 3 months of active brand building.
              Complete journal entries to unlock sooner!
            </p>

            <div className="bg-[#1C1C1C] p-6 mb-8">
              <div className="flex items-center justify-between mb-4">
                <span className="text-[#A1A1AA] text-sm">Progress to unlock</span>
                <span className="text-[#D4AF37] font-accent">
                  {eligibility?.journal_entries || 0} / 3 entries
                </span>
              </div>
              <Progress 
                value={(eligibility?.journal_entries || 0) / 3 * 100} 
                className="h-2 bg-[#27272A]"
              />
              <p className="text-[#52525B] text-xs mt-3">
                {eligibility?.message}
              </p>
            </div>

            <Button
              onClick={() => window.location.href = '/monthly-journal'}
              className="btn-gold px-8 py-4"
            >
              Start Your Journal
            </Button>
          </motion.div>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout title="Quarterly Vision Review">
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="card-premium p-8 mb-8"
        >
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-6">
            <div>
              <Compass className="text-[#D4AF37] mb-4" size={32} />
              <h2 className="font-serif text-2xl text-white mb-2">
                Clierxter Quarterly Vision Meeting
              </h2>
              <p className="text-[#A1A1AA]">
                Get AI-powered insights on your brand journey and strategic recommendations for the next quarter.
              </p>
            </div>
            
            <Button
              onClick={handleGenerateReview}
              disabled={generating}
              className="btn-gold px-8 py-4 whitespace-nowrap flex items-center gap-2"
              data-testid="generate-review"
            >
              {generating ? (
                <>
                  <Loader2 className="animate-spin" size={18} />
                  Analyzing...
                </>
              ) : (
                <>
                  <Compass size={18} />
                  Generate New Review
                </>
              )}
            </Button>
          </div>
        </motion.div>

        {currentReview ? (
          <div className="space-y-6">
            {/* Overall Score */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.1 }}
              className="card-premium p-8 text-center"
            >
              <span className="text-[#E5C585] text-xs font-accent tracking-widest">
                {currentReview.quarter} BRAND HEALTH
              </span>
              <div className="font-serif text-7xl text-[#D4AF37] mt-4">
                {currentReview.overall_score}/10
              </div>
              <p className="text-[#A1A1AA] mt-2">
                Next Focus: <span className="text-white">{currentReview.next_focus_area}</span>
              </p>
            </motion.div>

            {/* Celebrations */}
            {currentReview.celebrations?.length > 0 && (
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.15 }}
                className="card-premium p-6 bg-gradient-to-r from-[#D4AF37]/10 to-transparent"
              >
                <div className="flex items-center gap-3 mb-4">
                  <Trophy className="text-[#D4AF37]" size={24} />
                  <h3 className="font-serif text-xl text-white">Your Wins This Quarter</h3>
                </div>
                <ul className="space-y-3">
                  {currentReview.celebrations.map((win, i) => (
                    <li key={i} className="flex items-start gap-3">
                      <Star className="text-[#D4AF37] mt-1" size={16} />
                      <span className="text-[#E4E4E7]">{win}</span>
                    </li>
                  ))}
                </ul>
              </motion.div>
            )}

            {/* Brand Insights */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2 }}
              className="card-premium p-6"
            >
              <div className="flex items-center gap-3 mb-4">
                <Lightbulb className="text-[#E5C585]" size={24} />
                <h3 className="font-serif text-xl text-white">Brand Performance Insights</h3>
              </div>
              <ul className="space-y-3">
                {currentReview.brand_insights?.map((insight, i) => (
                  <li key={i} className="flex items-start gap-3">
                    <CheckCircle className="text-green-400 mt-1" size={16} />
                    <span className="text-[#A1A1AA]">{insight}</span>
                  </li>
                ))}
              </ul>
            </motion.div>

            {/* Content Review */}
            {currentReview.content_review && (
              <div className="grid md:grid-cols-2 gap-6">
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.25 }}
                  className="card-premium p-6"
                >
                  <div className="flex items-center gap-3 mb-4">
                    <CheckCircle className="text-green-400" size={20} />
                    <h4 className="font-serif text-lg text-white">Content Strengths</h4>
                  </div>
                  <ul className="space-y-2">
                    {currentReview.content_review.strengths?.map((s, i) => (
                      <li key={i} className="text-[#A1A1AA] flex items-start gap-2">
                        <span className="text-[#D4AF37]">•</span> {s}
                      </li>
                    ))}
                  </ul>
                </motion.div>

                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.3 }}
                  className="card-premium p-6"
                >
                  <div className="flex items-center gap-3 mb-4">
                    <AlertCircle className="text-[#E5C585]" size={20} />
                    <h4 className="font-serif text-lg text-white">Opportunities</h4>
                  </div>
                  <ul className="space-y-2">
                    {currentReview.content_review.opportunities?.map((o, i) => (
                      <li key={i} className="text-[#A1A1AA] flex items-start gap-2">
                        <span className="text-[#D4AF37]">•</span> {o}
                      </li>
                    ))}
                  </ul>
                </motion.div>
              </div>
            )}

            {/* Strategy Adjustments */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.35 }}
              className="card-premium p-6"
            >
              <div className="flex items-center gap-3 mb-4">
                <TrendingUp className="text-[#D4AF37]" size={24} />
                <h3 className="font-serif text-xl text-white">Strategy Adjustments</h3>
              </div>
              <ul className="space-y-3">
                {currentReview.strategy_adjustments?.map((adj, i) => (
                  <li key={i} className="flex items-start gap-3">
                    <span className="text-[#D4AF37] font-serif">{i + 1}.</span>
                    <span className="text-[#A1A1AA]">{adj}</span>
                  </li>
                ))}
              </ul>
            </motion.div>

            {/* New Goals */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.4 }}
              className="card-premium p-6"
            >
              <div className="flex items-center gap-3 mb-4">
                <Target className="text-[#E5C585]" size={24} />
                <h3 className="font-serif text-xl text-white">Goals for Next Quarter</h3>
              </div>
              <div className="grid md:grid-cols-2 gap-4">
                {currentReview.new_goals?.map((goal, i) => (
                  <div key={i} className="flex items-start gap-3 p-4 bg-[#1C1C1C] border border-[#27272A]">
                    <div className="w-8 h-8 flex items-center justify-center bg-[#D4AF37]/10 text-[#D4AF37] font-serif">
                      {i + 1}
                    </div>
                    <span className="text-[#E4E4E7] flex-1">{goal}</span>
                  </div>
                ))}
              </div>
            </motion.div>

            {/* Review Date */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.45 }}
              className="flex items-center justify-center gap-2 text-[#52525B] text-sm"
            >
              <Calendar size={14} />
              <span>Generated on {new Date(currentReview.created_at).toLocaleDateString()}</span>
            </motion.div>
          </div>
        ) : (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="card-premium p-12 text-center"
          >
            <Compass className="text-[#27272A] mx-auto mb-6" size={64} />
            <h3 className="font-serif text-xl text-white mb-3">No Reviews Yet</h3>
            <p className="text-[#A1A1AA] max-w-md mx-auto mb-8">
              Click "Generate New Review" to get your first quarterly vision analysis with personalized insights and goals.
            </p>
          </motion.div>
        )}

        {/* Previous Reviews */}
        {reviews.length > 1 && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.5 }}
            className="mt-12"
          >
            <h3 className="font-serif text-xl text-white mb-4">Previous Reviews</h3>
            <div className="grid md:grid-cols-3 gap-4">
              {reviews.slice(1).map((review) => (
                <button
                  key={review.review_id}
                  onClick={() => setCurrentReview(review)}
                  className="card-premium p-4 text-left hover:border-[#D4AF37]/50 transition-colors"
                >
                  <p className="text-[#D4AF37] text-xs font-accent">{review.quarter}</p>
                  <p className="text-white font-serif text-2xl mt-1">{review.overall_score}/10</p>
                  <p className="text-[#52525B] text-xs mt-2">{review.next_focus_area}</p>
                </button>
              ))}
            </div>
          </motion.div>
        )}
      </div>
    </DashboardLayout>
  );
};

export default QuarterlyReview;
